import React, { Component } from "react";
import currency from "currency.js";
import { connect } from "react-redux";
import { withStyles } from "material-ui/styles";
import CheckCircle from "material-ui-icons/CheckCircle";
import Print from "material-ui-icons/Print";
import FullPageDialog from "../../../controls/dialog/FullPageDialog";
import api from "../../../../api";
import Message from "../../../controls/Message";
import { getCartItemsArraySelector } from "../../../../selectors";
import NormalSaleForm from "./NormalSaleForm";
import CircularLoader from "../../../controls/loader/CircularLoader";
import {
  initTransaction,
  cancelTransaction
} from "../../../../actions/transaction";
import { emptyCart } from "../../../../actions/cart";
import YesNo from "../../../controls/dialog/YesNo";
import printReceipt from "./Receipt";

const styles = () => ({
  message: {
    margin: 0,
    paddingBottom: "10px"
  },
  formContainer: {
    padding: "30px",
    margin: "auto",
    width: "500px",
    height: "100%"
  },
  successContainer: {
    minHeight: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center"
  },
  successCard: {
    width: "100%",
    padding: "40px 30px",
    borderRadius: "18px",
    background: "#ffffff",
    boxShadow: "0 12px 35px rgba(0, 0, 0, 0.10)"
  },
  successIcon: {
    width: "72px",
    height: "72px",
    margin: "0 auto 20px",
    borderRadius: "50%",
    background: "#dcfce7",
    color: "#16a34a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "38px",
    fontWeight: "800"
  },
  successTitle: {
    margin: "0 0 10px",
    fontSize: "28px",
    fontWeight: "800",
    color: "#111827"
  },
  successText: {
    margin: "0 0 25px",
    color: "#6b7280",
    fontSize: "15px"
  },
  successAmount: {
    margin: "0 0 25px",
    fontSize: "30px",
    fontWeight: "800",
    color: "#111827"
  },
  newSaleButton: {
    border: "none",
    borderRadius: "10px",
    padding: "13px 24px",
    background: "#111827",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer"
  }
});

class NormalSale extends Component {
  state = {
    error: "",
    showMessage: false,
    showConfirmDeleteDialog: false,
    isLoading: false,
    isSubmitting: false,
    saleCompleted: false,
    saleResult: null,
    errors: {},
    data: {
      balanceToPay: "0.00",
      amountPaid: "",
      paymentMethod: "CASH",
      splitPaymentMethod: "CASH",
      payments: []
    }
  };

  async componentDidMount() {
    try {
      if (this.props.transaction.id) {
        return;
      }

      this.setState({ isLoading: true });

      const res = await api.transaction.getTransactionId();
      const transId = res.data;
      this.props.initTransaction(transId);

      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({
        error: error.message,
        showMessage: true,
        isLoading: false
      });
    }
  }

  onChange = e => {
    const amountPaid = e.target.value;
    const { netTotal } = this.props.cart.summary;
    const balance = currency(amountPaid).subtract(netTotal);

    if (this.state.data.paymentMethod === "SPLIT") {
      this.setState({
        errors: {},
        data: {
          ...this.state.data,
          amountPaid
        }
      });
      return;
    }

    if (balance.value > 0) {
      this.setState({
        errors: {},
        data: {
          ...this.state.data,
          amountPaid,
          balanceToPay: balance.toString()
        }
      });
    } else {
      this.setState({
        errors: {},
        data: {
          ...this.state.data,
          amountPaid,
          balanceToPay: "0.00"
        }
      });
    }
  };

  onSplitPaymentMethodChange = splitPaymentMethod => {
    this.setState({
      errors: {},
      data: {
        ...this.state.data,
        splitPaymentMethod,
        amountPaid: ""
      }
    });
  };

  onAddSplitPayment = () => {
    const {
      amountPaid,
      paymentMethod,
      splitPaymentMethod,
      payments
    } = this.state.data;

    const amount = currency(amountPaid).value;

    if (!amount || amount <= 0) {
      this.setState({
        errors: {
          amountPaid: "Enter an amount before adding the payment."
        }
      });
      return;
    }

    const { netTotal } = this.props.cart.summary;

    const currentTotal = payments.reduce(
      (total, payment) => total + Number(payment.amount || 0),
      0
    );

    const newTotal = currency(currentTotal).add(amount).value;

    if (newTotal > Number(netTotal)) {
      this.setState({
        errors: {
          amountPaid: "The split payments cannot exceed the total bill."
        }
      });
      return;
    }

    const updatedPayments = [
      ...payments,
      {
        paymentMethod: splitPaymentMethod,
        amount
      }
    ];

    const remaining = currency(netTotal).subtract(newTotal).value;

    this.setState({
      errors: {},
      data: {
        ...this.state.data,
        payments: updatedPayments,
        amountPaid: "",
        balanceToPay: remaining > 0 ? String(remaining) : "0.00"
      }
    });
  };

  onRemoveSplitPayment = index => {
    const payments = this.state.data.payments.filter(
      (_, paymentIndex) => paymentIndex !== index
    );

    const { netTotal } = this.props.cart.summary;

    const totalPaid = payments.reduce(
      (total, payment) => total + Number(payment.amount || 0),
      0
    );

    const remaining = currency(netTotal).subtract(totalPaid).value;

    this.setState({
      errors: {},
      data: {
        ...this.state.data,
        payments,
        balanceToPay: remaining > 0 ? String(remaining) : "0.00"
      }
    });
  };

  onPaymentMethodChange = paymentMethod => {
    const { netTotal } = this.props.cart.summary;

    this.setState({
      errors: {},
      data: {
        ...this.state.data,
        paymentMethod,
        amountPaid:
          paymentMethod === "CASH"
            ? ""
            : paymentMethod === "SPLIT"
            ? ""
            : String(netTotal),
        balanceToPay: paymentMethod === "SPLIT" ? String(netTotal) : "0.00",
        payments: paymentMethod === "SPLIT" ? [] : []
      }
    });
  };

  onNormalSaleFormSubmit = async e => {
    e.preventDefault();

    if (this.state.isSubmitting || this.state.saleCompleted) {
      return;
    }

    const {
      amountPaid,
      paymentMethod,


      payments
    } = this.state.data;

    const { cart, transaction } = this.props;

    const { summary } = cart;
    const { netTotal } = summary;

    if (!transaction.id) {
      this.setState({
        error: "No valid transaction is available. Please start a new sale.",
        showMessage: true
      });
      return;
    }

    let finalAmountPaid = currency(amountPaid || 0).value;
    let splitPayments = undefined;

    if (paymentMethod === "SPLIT") {
      splitPayments = payments;

      finalAmountPaid = payments.reduce(
        (total, payment) =>
          currency(total).add(payment.amount).value,
        0
      );

      if (payments.length < 2) {
        this.setState({
          errors: {
            amountPaid: "Add at least two payment methods for a split payment."
          }
        });
        return;
      }

      if (currency(finalAmountPaid).value < currency(netTotal).value) {
        this.setState({
          errors: {
            amountPaid: "The split payments must cover the full bill."
          }
        });
        return;
      }
    } else {
      const balance = currency(amountPaid).subtract(netTotal);

      if (balance.value < 0) {
        this.setState({
          errors: {
            amountPaid:
              "You have entered a less amount than the bill. Please correct it"
          }
        });

        return;
      }
    }

    const selectedCashierId = localStorage.getItem("posSelectedCashierId") || "";

    const sale = {
      transactionId: transaction.id,
      cashierId: selectedCashierId,
      totalDiscount: currency(summary.discountOnItems)
        .add(summary.discountOnTotal)
        .value,
      tax: currency(summary.taxAmount).value,
      taxPercentageString: String(summary.tax),
      amountPaid: finalAmountPaid,
      paymentMethod,

      items: this.props.cartArray.map(item => ({
        productId: String(item.id),
        name: item.name,
        qty: Number(item.qty),
        price: currency(item.price).value,
        sellingPrice: currency(item.sellingPrice).value,
        discount: currency(item.discount).value,
        totalPrice: currency(item.totalPrice).value
      }))
    };

    if (paymentMethod === "SPLIT") {
      sale.payments = splitPayments;
    }

    this.setState({
      isSubmitting: true,
      errors: {},
      error: "",
      showMessage: false
    });

    try {
      const res = await api.transaction.saveNormalSale(
        transaction.id,
        sale
      );

      console.log(res.data);

      this.setState({
        isSubmitting: false,
        saleCompleted: true,
        saleResult: res.data
      });
    } catch (error) {
      console.error("Normal sale failed:", error);

      this.setState({
        isSubmitting: false,
        error:
          error.response &&
          error.response.data &&
          error.response.data.message
            ? error.response.data.message
            : error.message,
        showMessage: true
      });
    }
  };

  onNormalSaleFormCancel = () => {
    if (this.state.isSubmitting) {
      return;
    }

    this.setState({ showConfirmDeleteDialog: true });
  };

  onMessageCloseClick = () => {
    this.setState({ showMessage: false });
  };

  onYesNoPopYesClick = () => {
    if (this.state.isSubmitting) {
      return;
    }

    this.props.cancelTransaction();
    this.props.emptyCart();

    this.setState({
      showConfirmDeleteDialog: false
    });

    this.props.handleClose();
  };

  onYesNoPopNoClick = () => {
    this.setState({ showConfirmDeleteDialog: false });
  };

  onPrintReceipt = () => {
    const { transaction, cart } = this.props;
    const { data } = this.state;

    let cashierName = "Cashier";

    try {
      cashierName =
        localStorage.getItem("posSelectedCashierName") ||
        "Cashier";
    } catch (error) {
      console.warn("Could not read selected cashier:", error);
    }

    printReceipt({
      transactionId: transaction.id,
      cart,
      paymentMethod: data.paymentMethod,
      amountPaid:
        data.paymentMethod === "SPLIT"
          ? data.payments.reduce(
              (total, payment) =>
                currency(total).add(payment.amount).value,
              0
            )
          : data.amountPaid,
      cashierName,
      splitPayments: data.payments || []
    });
  };
  onStartNewSale = () => {
    this.props.cancelTransaction();
    this.props.emptyCart();

    this.setState({
      saleCompleted: false,
      saleResult: null,
      error: "",
      showMessage: false,
      isSubmitting: false,
      data: {
        balanceToPay: "0.00",
        amountPaid: "",
        paymentMethod: "CASH",
        splitPaymentMethod: "CASH",
        payments: []
      }
    });

    this.props.handleClose();
  };

  renderSuccess = () => {
    const { classes, cart } = this.props;
    const { saleResult } = this.state;

    const total = cart.summary.netTotal;

    return (
      <div className={classes.successContainer}>
        <div className={classes.successCard}>
          <div className={classes.successIcon}>
            <CheckCircle />
          </div>

          <h2 className={classes.successTitle}>
            Sale Completed
          </h2>

          <p className={classes.successText}>
            {saleResult && saleResult.message
              ? saleResult.message
              : "Sale completed successfully."}
          </p>

          <div className={classes.successAmount}>
            {"\u20A6"}{Number(total || 0).toLocaleString()}
          </div>

          <button
            type="button"
            className={classes.newSaleButton}
            onClick={this.onPrintReceipt}
          >
            <Print style={{ marginRight: 8 }} />
            Print Receipt
          </button>

          <button
            type="button"
            className={classes.newSaleButton}
            onClick={this.onStartNewSale}
          >
            Start New Sale
          </button>
        </div>
      </div>
    );
  };

  renderForm = () => {
    const { transaction, cart } = this.props;

    const {
      isLoading,
      isSubmitting,
      errors,
      data
    } = this.state;

    if (isLoading === true) {
      return null;
    }

    return (
      <NormalSaleForm
        cart={cart}
        errors={errors}
        data={data}
        onSubmit={this.onNormalSaleFormSubmit}
        onCancel={this.onNormalSaleFormCancel}
        onChange={this.onChange}
        onPaymentMethodChange={this.onPaymentMethodChange}
        onSplitPaymentMethodChange={this.onSplitPaymentMethodChange}
        onAddSplitPayment={this.onAddSplitPayment}
        onRemoveSplitPayment={this.onRemoveSplitPayment}
        transactionId={transaction.id}
        isSubmitting={isSubmitting}
      />
    );
  };

  render() {
    const { handleClose, open, classes } = this.props;

    const {
      error,
      showMessage,
      isLoading,
      isSubmitting,
      saleCompleted,
      showConfirmDeleteDialog
    } = this.state;

    return (
      <FullPageDialog
        open={open}
        handleClose={isSubmitting ? () => {} : handleClose}
        title={saleCompleted ? "Sale Completed" : "Normal Sale"}
      >
        <CircularLoader isLoading={isLoading || isSubmitting} />

        <YesNo
          open={showConfirmDeleteDialog}
          message="Are you sure wan't to cancel the transaction and clear the cart?"
          onOk={() => this.onYesNoPopYesClick()}
          onCancel={() => this.onYesNoPopNoClick()}
        />

        <div className={classes.formContainer}>
          {saleCompleted ? (
            this.renderSuccess()
          ) : (
            <React.Fragment>
              <Message
                className={classes.message}
                title="Got an error"
                message={error}
                show={showMessage}
                isError={true}
                onCloseClick={this.onMessageCloseClick}
              />

              {this.renderForm()}
            </React.Fragment>
          )}
        </div>
      </FullPageDialog>
    );
  }
}

function mapStateToProps(state) {
  return {
    transaction: state.transaction,
    cart: state.cart,
    cartArray: getCartItemsArraySelector(state)
  };
}

const mapDispatchToProps = {
  initTransaction,
  cancelTransaction,
  emptyCart
};

const component = withStyles(styles)(NormalSale);

export default connect(mapStateToProps, mapDispatchToProps)(component);




















